import { useState } from 'react';
import { BlockRenderer, type CmsBlock } from './BlockRenderer';
import { useEditMode } from '../../contexts/EditModeContext';

interface AccordionItem {
  title: string;
}

interface AccordionProps {
  title?: string;
  items?: AccordionItem[];
}

/**
 * AccordionBlock — container block like Row/Columns.
 * Each item in `props.items` defines a collapsible section.
 * Child blocks are assigned to sections via `props._col` (same pattern as Row).
 */
export function AccordionBlock({ block }: { block: CmsBlock }) {
  const { isEditMode } = useEditMode();
  const { title, items = [] } = (block.props ?? {}) as AccordionProps;
  const children = block.children ?? [];
  const [openIndex, setOpenIndex] = useState<number | null>(isEditMode ? 0 : null);

  // Group children by _col (same pattern as RowBlock)
  const sections = items.map((item, i) => ({
    ...item,
    blocks: children
      .filter((c) => ((c.props._col as number) ?? 0) === i)
      .sort((a, b) => a.sortOrder - b.sortOrder),
  }));

  if (sections.length === 0 && !isEditMode) return null;

  return (
    <div className="py-4">
      {title && <h2 className="text-xl font-bold mb-4">{title}</h2>}
      <div className="divide-y divide-zinc-200 border border-zinc-200 rounded-lg overflow-hidden">
        {sections.map((section, i) => {
          const isOpen = openIndex === i;
          return (
            <div key={i}>
              <button
                onClick={() => setOpenIndex(isOpen ? null : i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-zinc-50 transition-colors"
              >
                <span className="font-medium text-zinc-900">{section.title || `Section ${i + 1}`}</span>
                <svg
                  className={`w-4 h-4 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isOpen && (
                <div className="px-5 pb-4">
                  {section.blocks.length > 0 && (
                    <div className="space-y-4">
                      {section.blocks.map((child) => (
                        <BlockRenderer key={child.id} block={child} />
                      ))}
                    </div>
                  )}
                  {isEditMode && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.parent.postMessage({
                          type: 'ADD_BLOCK_TO_COLUMN',
                          parentBlockId: block.id,
                          column: i,
                        }, '*');
                      }}
                      className={`w-full border-2 border-dashed border-zinc-300 hover:border-indigo-400 rounded-lg p-3 text-center text-zinc-400 hover:text-indigo-500 text-xs transition-colors cursor-pointer ${section.blocks.length > 0 ? 'mt-2' : ''}`}
                    >
                      + Add to "{section.title || `Section ${i + 1}`}"
                    </button>
                  )}
                  {!isEditMode && section.blocks.length === 0 && (
                    <p className="text-sm text-zinc-400 italic">No content</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {isEditMode && sections.length === 0 && (
        <div className="border-2 border-dashed border-indigo-300 bg-indigo-50 rounded-lg px-4 py-6 text-center">
          <div className="text-sm font-semibold text-indigo-500">Accordion</div>
          <div className="text-xs text-indigo-400 mt-1">Add items in the config panel first</div>
        </div>
      )}
    </div>
  );
}
